import { useState } from 'react';
import PropTypes from 'prop-types';
import { useEventListener } from 'usehooks-ts';
import { List, ListItem, ListItemButton, Tooltip, Typography } from '@mui/material';

import { HEADER_HEIGHT, SIDEBAR_WIDTH } from 'constants/components';

const styles = {
  sidebar: ({ palette, zIndex }, sidebarHeight) => ({
    backgroundColor: palette.text.primary,
    width: SIDEBAR_WIDTH,
    left: 0,
    zIndex: zIndex.drawer,
    paddingTop: 8,
    position: 'fixed',
    top: HEADER_HEIGHT,
    height: sidebarHeight - HEADER_HEIGHT,
  }),
  listItem: { paddingInline: 'unset' },
  tooltip: ({ palette }) => ({
    '.MuiTooltip-arrow': { color: palette.text.primary },
    '.MuiTooltip-tooltip': {
      backgroundColor: palette.text.primary,
      padding: 2,
      borderRadius: '10px',
    },
  }),
  sidebarButton: ({ palette }, isActive) => ({
    justifyContent: 'center',
    paddingInline: 'unset',
    color: isActive ? palette.common.white : palette.colors.darkGrey,
  }),
};

const Sidebar = ({ sidebarItems }) => {
  const [sidebarHeight, setSidebarHeight] = useState(window.innerHeight);

  useEventListener('resize', () => {
    setSidebarHeight(window.innerHeight);
  });

  if (!sidebarItems.length) {
    return null;
  }

  return (
    <nav aria-label="Sidebar navigation" className="remove_for_pdf">
      <List sx={(theme) => styles.sidebar(theme, sidebarHeight)}>
        {sidebarItems.map(({ itemKey, title, Icon, isActive, isDisabled, handleItemClick }) => (
          <ListItem key={itemKey} sx={styles.listItem}>
            <Tooltip
              arrow
              disableFocusListener
              disableTouchListener
              disableHoverListener={isDisabled}
              placement="right"
              slotProps={{ popper: { sx: styles.tooltip } }}
              title={
                <Typography variant="button" color="text.secondary">
                  {title}
                </Typography>
              }
            >
              <ListItemButton
                onClick={handleItemClick}
                disabled={isDisabled}
                id={`sidebar-button-${itemKey}`}
                sx={(theme) => styles.sidebarButton(theme, isActive)}
                aria-label={title}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon aria-hidden="true" />
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>
    </nav>
  );
};

Sidebar.propTypes = {
  sidebarItems: PropTypes.arrayOf(
    PropTypes.shape({
      itemKey: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      Icon: PropTypes.func.isRequired,
      isActive: PropTypes.bool.isRequired,
      isDisabled: PropTypes.bool.isRequired,
      handleItemClick: PropTypes.func.isRequired,
    }),
  ).isRequired,
};

export default Sidebar;
